import React from 'react';
import { Clock, Key, CheckCircle, XCircle, AlertTriangle, Shield, Eye, Edit, Search } from 'lucide-react';
import { ApiKey } from '../services/api';
import './ApiKeyCard.css';

interface ApiKeyCardProps {
  apiKey: ApiKey;
  isSelected: boolean;
  onClick: () => void;
  isCreating?: boolean;
}

const ApiKeyCard: React.FC<ApiKeyCardProps> = ({
  apiKey,
  isSelected,
  onClick,
  isCreating = false
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getStatusIcon = () => {
    if (apiKey.revoked_at) {
      return <XCircle size={14} className="status-icon revoked" />;
    }
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) {
      return <AlertTriangle size={14} className="status-icon expired" />;
    }
    if (apiKey.is_active) {
      return <CheckCircle size={14} className="status-icon active" />;
    }
    return <AlertTriangle size={14} className="status-icon" />;
  };

  const getStatusText = () => {
    if (apiKey.revoked_at) return 'Revoked';
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) return 'Expired';
    if (apiKey.is_active) return 'Active';
    return 'Inactive';
  };

  const getStatusClass = () => {
    if (apiKey.revoked_at) return 'revoked';
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) return 'expired';
    if (apiKey.is_active) return 'active';
    return 'inactive';
  };

  const getScopeIcon = (scope: string) => {
    switch (scope) {
      case 'admin':
        return <Shield size={12} />;
      case 'write':
        return <Edit size={12} />;
      case 'read':
        return <Eye size={12} />;
      case 'search':
        return <Search size={12} />;
      default:
        return <Key size={12} />;
    }
  };

  const getScopeClass = (scope: string) => {
    return `scope-${scope}`;
  };

  return (
    <div className="apikey-card-wrapper">
      <div
        className={`apikey-card ${isSelected ? 'selected' : ''} ${isCreating ? 'creating' : ''} ${getStatusClass()}`}
        onClick={onClick}
      >
        {/* Top color bar */}
        <div className="apikey-card-colorbar" />

        {/* Header */}
        <div className="apikey-card-header">
          <div className="apikey-card-title">
            <Key size={16} className="key-icon" />
            <span className="apikey-name">{apiKey.name || 'New API Key'}</span>
          </div>
          <div className="apikey-status-badge">
            {getStatusIcon()}
            <span className="status-text">{getStatusText()}</span>
          </div>
        </div>

        {/* Key Prefix */}
        <div className="apikey-card-prefix">
          <code>{apiKey.key_prefix || 'sk_live_********'}...</code>
        </div>

        {/* Scopes Section */}
        <div className="apikey-card-scopes-section">
          <div className="scopes-label">Scopes</div>
          <div className="scopes-list">
            {!apiKey.scopes || apiKey.scopes.length === 0 ? (
              <div className="no-scopes">No scopes assigned</div>
            ) : (
              apiKey.scopes.map((scope, index) => (
                <div key={`${scope}-${index}`} className={`scope-badge ${getScopeClass(scope)}`}>
                  {getScopeIcon(scope)}
                  <span>{scope}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="apikey-card-footer">
          <div className="apikey-card-meta">
            <Clock size={12} />
            <span>{isCreating ? 'Draft' : formatDate(apiKey.created_at)}</span>
          </div>
          <div className="apikey-card-last-used">
            {apiKey.last_used_at ? (
              <span className="last-used-time" title={`Last used: ${new Date(apiKey.last_used_at).toLocaleString()}`}>
                Used {formatRelativeTime(apiKey.last_used_at)}
              </span>
            ) : (
              <span className="never-used">Never used</span>
            )}
          </div>
        </div>

        {/* Expiration warning */}
        {apiKey.expires_at && !apiKey.revoked_at && (
          <div className={`apikey-card-expiry ${new Date(apiKey.expires_at) < new Date() ? 'expired' : ''}`}>
            <AlertTriangle size={12} />
            <span>
              {new Date(apiKey.expires_at) < new Date()
                ? `Expired ${formatRelativeTime(apiKey.expires_at)}`
                : `Expires ${formatDate(apiKey.expires_at)}`
              }
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeyCard;
